import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Fish, Loader2, MapPin, ExternalLink, ChevronDown, ChevronUp } from "lucide-react";
import { matchOperatorBadge } from "../../lib/operatorBadges";

/**
 * OperatorBadge — small chip showing the fishery's governing body / owner
 * (Angling Trust, Fish Legal, Environment Agency, Canal & River Trust…)
 * when OSM's `operator` tag matches a known UK body.
 */
function OperatorBadge({ operator, testid }) {
  const [failed, setFailed] = React.useState(false);
  const match = matchOperatorBadge(operator);
  if (!match || failed) return null;
  return (
    <span
      className="inline-flex items-center gap-1.5 bg-white border hairline px-2 py-1 text-[10px] mono uppercase tracking-wider text-[#333] mt-1.5"
      data-testid={testid || "fishery-operator-badge"}
      title={`Managed by ${match.name}`}
    >
      <img
        src={match.logoUrl}
        alt={`${match.name} logo`}
        className="w-4 h-4 object-contain shrink-0"
        loading="lazy"
        referrerPolicy="no-referrer"
        onError={() => setFailed(true)}
      />
      {match.name}
    </span>
  );
}

/**
 * iter121 — Fisheries nearby section.
 *
 * For each campsite we surface up to 5 managed fishing waters within 10 km
 * (OSM-sourced, 30-day cached on the backend). Expandable to show every
 * fishery within 10 km. Mirrors the WalksNearbySection structure so the
 * page rhythm stays consistent.
 */
export default function FisheriesNearbySection({ campsiteId, campsiteName }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const url = showAll
      ? `/campsites/${campsiteId}/fisheries?all=true`
      : `/campsites/${campsiteId}/fisheries`;
    (async () => {
      try {
        const { data } = await api.get(url);
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setError(e?.message || "Failed to load fisheries");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [campsiteId, showAll]);

  const items = data?.fisheries || [];
  const total = data?.total || 0;

  return (
    <div className="mb-10" data-testid="fisheries-nearby-section">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-3">
        <div>
          <div className="eyebrow text-[#0891b2] mb-1 inline-flex items-center gap-2">
            <Fish className="w-3 h-3" strokeWidth={2.4} /> Reel a fish near your pitch
          </div>
          <h3 className="font-display text-xl tracking-tight">
            Fisheries within 10 km
          </h3>
        </div>
        {total > 0 && (
          <div className="text-xs mono text-[#666] uppercase tracking-wider">
            {total} found · OpenStreetMap
          </div>
        )}
      </div>

      {loading && (
        <div className="border hairline bg-white p-6 flex items-center gap-3 text-sm text-[#666]" data-testid="fisheries-loading">
          <Loader2 className="w-4 h-4 animate-spin" />
          Finding fisheries near {campsiteName || "this campsite"}…
        </div>
      )}

      {!loading && error && (
        <div className="border hairline bg-[#fff8f5] p-4 text-sm text-[#7a2400]" data-testid="fisheries-error">
          Couldn't load fisheries right now — please try again later.
        </div>
      )}

      {!loading && !error && items.length === 0 && (
        <div className="border hairline bg-white p-4 text-sm text-[#666]" data-testid="fisheries-empty">
          No named fisheries found within 10 km of this campsite (OSM-sourced).
          If you know one nearby, let us know in the community thread and we'll
          add it.
        </div>
      )}

      {!loading && !error && items.length > 0 && (
        <>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="fisheries-list">
            {items.map((f) => (
              <li
                key={`${f.osm_type}-${f.osm_id}`}
                className="bg-white hairline p-3 hover:border-[#0891b2] transition-colors"
                data-testid={`fishery-${f.osm_id}`}
              >
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="font-display text-base leading-tight">{f.name}</div>
                  <div className="mono text-[10px] text-[#666] whitespace-nowrap shrink-0">
                    {f.distance_from_campsite_km != null
                      ? `${f.distance_from_campsite_km} km`
                      : ""}
                  </div>
                </div>
                {f.descriptors && f.descriptors.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {f.descriptors.slice(0, 3).map((d, i) => (
                      <span
                        key={i}
                        className="text-[10px] mono uppercase tracking-wider bg-[#e0f2fe] text-[#0c4a6e] px-1.5 py-0.5"
                      >
                        {d}
                      </span>
                    ))}
                  </div>
                )}
                {f.operator && (
                  <div>
                    <OperatorBadge operator={f.operator} testid={`fishery-operator-${f.osm_id}`} />
                  </div>
                )}
                <div className="flex items-center gap-3 mt-2 text-[11px] text-[#0891b2]">
                  <a
                    href={`https://www.openstreetmap.org/${f.osm_type}/${f.osm_id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 hover:underline"
                  >
                    <MapPin className="w-3 h-3" /> View on map
                  </a>
                  {f.website && (
                    <a
                      href={f.website}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 hover:underline"
                    >
                      <ExternalLink className="w-3 h-3" /> Website
                    </a>
                  )}
                </div>
              </li>
            ))}
          </ul>
          {total > items.length && !showAll && (
            <button
              onClick={() => setShowAll(true)}
              className="mt-3 inline-flex items-center gap-1 text-xs mono uppercase tracking-wider text-[#0891b2] hover:underline"
              data-testid="fisheries-show-all"
            >
              <ChevronDown className="w-3 h-3" /> Show all {total} within 10 km
            </button>
          )}
          {showAll && total > 5 && (
            <button
              onClick={() => setShowAll(false)}
              className="mt-3 inline-flex items-center gap-1 text-xs mono uppercase tracking-wider text-[#666] hover:underline"
              data-testid="fisheries-show-fewer"
            >
              <ChevronUp className="w-3 h-3" /> Show fewer
            </button>
          )}
        </>
      )}
    </div>
  );
}
